import { getSsoVerifyBase } from "./apiBase";

export type SuiteUserProfile = {
  id: number;
  username: string;
  email: string | null;
  phone: string | null;
  client: { id: number; name: string; logo?: string | null } | null;
};

/** Lit `suite_user_id` / `userId` dans l’URL et aligne sessionStorage + localStorage. */
export function syncSuiteUserIdFromQuery(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const q = new URLSearchParams(window.location.search);
    const v = q.get("suite_user_id") || q.get("userId");
    if (v && /^\d+$/.test(v)) {
      clearChatLocalLogoutMark();
      sessionStorage.setItem("suite_user_id", v);
      localStorage.setItem("suite_user_id", v);
      localStorage.setItem("userId", v);
      window.dispatchEvent(new CustomEvent("suiteUserIdReceived", { detail: { userId: v } }));
      return v;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function readStoredSuiteUserId(): string | null {
  if (typeof window.readStoredSuiteUserId === "function") {
    const fromShared = window.readStoredSuiteUserId();
    if (fromShared) return String(fromShared);
  }
  try {
    const local = localStorage.getItem("suite_user_id") || localStorage.getItem("userId");
    if (!local || !/^\d+$/.test(local)) {
      sessionStorage.removeItem("suite_user_id");
      return null;
    }
    return local;
  } catch {
    return null;
  }
}

/** Chat dans le hub : même origine que la suite → localStorage partagé. */
export function isChatEmbeddedInHub(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.parent !== window;
  } catch {
    return false;
  }
}

const CHAT_LOCAL_LOGOUT_KEY = "chat_local_logout";

export function markChatLocalLogout(): void {
  try {
    sessionStorage.setItem(CHAT_LOCAL_LOGOUT_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function isChatLocalLogout(): boolean {
  try {
    return sessionStorage.getItem(CHAT_LOCAL_LOGOUT_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearChatLocalLogoutMark(): void {
  try {
    sessionStorage.removeItem(CHAT_LOCAL_LOGOUT_KEY);
  } catch {
    /* ignore */
  }
}

export function clearStoredSuiteUserIds(): void {
  clearChatLocalLogoutMark();
  if (typeof window.clearSuiteStoredIdentity === "function") {
    window.clearSuiteStoredIdentity();
    return;
  }
  try {
    sessionStorage.removeItem("suite_user_id");
    localStorage.removeItem("suite_user_id");
    localStorage.removeItem("userId");
  } catch {
    /* ignore */
  }
}

const SUITE_SESSION_ERRORS = new Set(["session_required", "session_or_userid_required", "unauthorized"]);

export function isSuiteSessionErrorCode(error?: string | null): boolean {
  return typeof error === "string" && SUITE_SESSION_ERRORS.has(error);
}

export function resetChatSessionGateEmit(): void {
  if (typeof window.resetSuiteSessionGateEmit === "function") {
    window.resetSuiteSessionGateEmit();
  }
}

export async function fetchSsoVerify(suiteUserId: string): Promise<{
  ok: boolean;
  forbidden?: boolean;
  profile?: SuiteUserProfile;
  message?: string;
}> {
  const base = getSsoVerifyBase();
  if (!base) {
    return { ok: false, message: "API suite introuvable (URL ou VITE_API_SUITE_BASE)." };
  }
  const url = `${base}/sso-verify.php?suite_user_id=${encodeURIComponent(suiteUserId)}&app=${encodeURIComponent("Chat")}`;
  try {
    const ctrl = new AbortController();
    const t = window.setTimeout(() => ctrl.abort(), 15000);
    const res = await fetch(url, { method: "GET", credentials: "omit", cache: "no-store", signal: ctrl.signal });
    window.clearTimeout(t);
    let data: Record<string, unknown>;
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      return { ok: false, message: `Réponse non JSON (${res.status}). Vérifier l’URL api_suite : ${base}` };
    }
    if (!res.ok || !data.success) {
      const err = typeof data.error === "string" ? data.error : "";
      const forbidden =
        res.status === 403 || err === "forbidden_app_permission" || err === "admin_apps_disabled";
      return {
        ok: false,
        forbidden,
        message: typeof data.message === "string" ? data.message : `HTTP ${res.status}`,
      };
    }
    const u = data.user as Record<string, unknown> | undefined;
    if (!u) {
      return { ok: false, message: "Réponse SSO invalide." };
    }
    const idNum = typeof u.id === "number" ? u.id : parseInt(String(u.id), 10);
    if (!Number.isFinite(idNum) || idNum <= 0) {
      return { ok: false, message: "Réponse SSO invalide." };
    }
    const profile: SuiteUserProfile = {
      id: idNum,
      username: String(u.username ?? ""),
      email: (u.email as string) ?? null,
      phone: (u.phone as string) ?? null,
      client: (u.client as SuiteUserProfile["client"]) ?? null,
    };
    return { ok: true, profile };
  } catch (e) {
    const aborted =
      (e instanceof Error && e.name === "AbortError") ||
      (typeof DOMException !== "undefined" && e instanceof DOMException && e.name === "AbortError");
    if (aborted) {
      return { ok: false, message: "Délai dépassé (15 s) — vérifier l’URL api_suite et le réseau." };
    }
    return { ok: false, message: e instanceof Error ? e.message : "Erreur réseau" };
  }
}
